import * as THREE from '@phdsoft/three';
import { Pass, FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js';

/** Render layer reserved for primary (cyan) selection. */
export const OUTLINE_LAYER_PRIMARY = 1;
/** Render layer reserved for secondary (teal) multi-selection. */
export const OUTLINE_LAYER_SECONDARY = 2;

/** Loop bound for the dilation kernel — also caps how far `edgeThickness` can be tuned. */
const MAX_RADIUS = 4;

/**
 * Lightweight replacement for two stock `THREE.OutlinePass` instances.
 *
 * The stock pass, per active instance per frame, does a full-scene depth re-render,
 * two full scene-graph traversals to toggle visibility, a mask render, and four
 * separable-blur draws at 2× DPR — all to draw a ~2px X-ray outline. Because this
 * app uses `hiddenEdgeColor === visibleEdgeColor` (a pure see-through outline), the
 * depth re-render that distinguishes occluded vs visible edges is wasted work.
 *
 * This pass instead:
 *  - isolates the selected meshes with render **Layers** (toggled only on selection
 *    change), so the mask render draws *only* the selected meshes — no per-frame
 *    scene traversal and no whole-scene depth render;
 *  - draws one tiny mask RT (primary coverage in `.r`, secondary in `.g`);
 *  - runs a single full-screen dilation/composite shader that overlays cyan on
 *    primary edges and teal on secondary edges directly onto the scene color in
 *    `readBuffer` (`needsSwap = false`, exactly like the stock overlay).
 *
 * The outline is a pure X-ray (always visible, never occluded by geometry), matching
 * the old behavior. An optional "occlude behind geometry" toggle was intentionally
 * left out: reading the scene depth would require sampling a depth texture attached to
 * `readBuffer`, which forms a GL framebuffer/texture feedback loop and aborts the draw.
 * Re-adding it would mean first resolving the scene depth into a standalone (unattached)
 * texture and sampling that copy.
 *
 * Non-invasive by design: it never swaps or mutates object materials, so it composes
 * with every existing material-state system (view modes, paint mode, the customMaterial
 * slot, polygon offsets). Selection targets may be individual meshes, Groups (flattened
 * to their descendant meshes), lightSection meshes, or pooled InstancedMesh proxy meshes
 * — all real `Object3D`/`Mesh`, so all work with the layer approach.
 */
export class SelectionOutlinePass extends Pass {
  public renderScene: THREE.Scene;
  public renderCamera: THREE.Camera;

  // --- public tunables -------------------------------------------------------
  /** Primary outline color (cyan). Matches the old primary `OutlinePass`. */
  public primaryColor: THREE.Color = new THREE.Color(0xccffff);
  /** Secondary outline color (teal). Matches the old secondary `OutlinePass` (0x247b5). */
  public secondaryColor: THREE.Color = new THREE.Color(0x0247b5);
  /** Outline thickness in device pixels (composite runs at the composer's 2× DPR size). */
  public edgeThickness: number = 3;
  /** Outline opacity (alpha of the overlaid edge fragments). */
  public opacity: number = 1.0;

  // --- membership (drives layer bits) ----------------------------------------
  /** Flattened set of meshes currently outlined in the primary channel. */
  private _primarySet: Set<THREE.Mesh> = new Set();
  /** Flattened set of meshes currently outlined in the secondary channel. */
  private _secondarySet: Set<THREE.Mesh> = new Set();

  // --- GPU resources ---------------------------------------------------------
  private _maskRT: THREE.WebGLRenderTarget;
  private _maskMaterialPrimary: THREE.ShaderMaterial;
  private _maskMaterialSecondary: THREE.ShaderMaterial;
  private _compositeMaterial: THREE.ShaderMaterial;
  private _fsQuad: FullScreenQuad;

  // --- scratch (avoid per-frame allocation) ----------------------------------
  private _oldClearColor: THREE.Color = new THREE.Color();
  private _flattenScratch: THREE.Mesh[] = [];

  constructor(resolution: THREE.Vector2, scene: THREE.Scene, camera: THREE.Camera) {
    super();

    this.renderScene = scene;
    this.renderCamera = camera;

    // Never the last pass (OutputPass follows), so we overlay onto readBuffer.
    this.needsSwap = false;

    const width = resolution ? resolution.x : 256;
    const height = resolution ? resolution.y : 256;

    // Mask RT: .r primary coverage, .g secondary coverage.
    // No depth buffer / no mipmaps — only the selected meshes ever draw into it.
    this._maskRT = new THREE.WebGLRenderTarget(width, height, {
      format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      depthBuffer: false,
    });
    this._maskRT.texture.name = 'SelectionOutlinePass.mask';
    this._maskRT.texture.generateMipmaps = false;

    this._maskMaterialPrimary = this._buildMaskMaterial(THREE.NoBlending, 'vec4(1.0, 0.0, 0.0, 1.0)');
    this._maskMaterialSecondary = this._buildMaskMaterial(THREE.AdditiveBlending, 'vec4(0.0, 1.0, 0.0, 1.0)');

    this._compositeMaterial = this._buildCompositeMaterial();
    this._fsQuad = new FullScreenQuad(this._compositeMaterial);
  }

  // ===========================================================================
  // Materials
  // ===========================================================================

  /**
   * Flat unlit mask material. The vertex shader mirrors the stock OutlinePass
   * `prepareMaskMaterial` chunk set so InstancedMesh / batched / morphed / skinned
   * geometry transforms correctly. The fragment writes a constant coverage color.
   */
  private _buildMaskMaterial(blending: THREE.Blending, fragColor: string): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      uniforms: {},
      vertexShader: /* glsl */ `
        #include <common>
        #include <batching_pars_vertex>
        #include <morphtarget_pars_vertex>
        #include <skinning_pars_vertex>

        void main() {
          #include <batching_vertex>
          #include <skinbase_vertex>
          #include <begin_vertex>
          #include <morphtarget_vertex>
          #include <skinning_vertex>
          #include <project_vertex>
        }
      `,
      fragmentShader: /* glsl */ `
        void main() {
          gl_FragColor = ${fragColor};
        }
      `,
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false,
      blending,
    });
  }

  /**
   * Single full-screen dilation + composite. A pixel is an outline fragment for a
   * channel when it is empty in that channel but a neighbor within `uThickness` px is
   * filled (outer dilation). Primary color wins where both channels are edges.
   */
  private _buildCompositeMaterial(): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      defines: { MAX_RADIUS },
      uniforms: {
        tMask: { value: null },
        uTexSize: { value: new THREE.Vector2() },
        uPrimaryColor: { value: this.primaryColor },
        uSecondaryColor: { value: this.secondaryColor },
        uThickness: { value: this.edgeThickness },
        uOpacity: { value: this.opacity },
      },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        varying vec2 vUv;
        uniform sampler2D tMask;
        uniform vec2 uTexSize;
        uniform vec3 uPrimaryColor;
        uniform vec3 uSecondaryColor;
        uniform float uThickness;
        uniform float uOpacity;

        void main() {
          vec2 texel = 1.0 / uTexSize;
          vec4 self = texture2D(tMask, vUv);

          bool selfPrimary = self.r > 0.5;
          bool selfSecondary = self.g > 0.5;

          bool primaryNear = false;
          bool secondaryNear = false;
          float r2 = uThickness * uThickness;

          for (int dy = -MAX_RADIUS; dy <= MAX_RADIUS; dy++) {
            for (int dx = -MAX_RADIUS; dx <= MAX_RADIUS; dx++) {
              if (dx == 0 && dy == 0) continue;
              float d2 = float(dx * dx + dy * dy);
              if (d2 > r2) continue;

              vec2 nUv = vUv + vec2(float(dx), float(dy)) * texel;
              vec4 n = texture2D(tMask, nUv);
              if (n.r > 0.5) primaryNear = true;
              if (n.g > 0.5) secondaryNear = true;
            }
          }

          bool primaryEdge = primaryNear && !selfPrimary;
          bool secondaryEdge = secondaryNear && !selfSecondary;

          if (!primaryEdge && !secondaryEdge) discard;

          vec3 color = primaryEdge ? uPrimaryColor : uSecondaryColor;
          gl_FragColor = vec4(color, uOpacity);
        }
      `,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.NormalBlending,
    });
  }

  // ===========================================================================
  // Membership API (drives layer bits; signatures map 1:1 to the graphics.ts helpers)
  // ===========================================================================

  /** Flatten an object to its descendant meshes (includes the object itself if a mesh). */
  private _flatten(obj: THREE.Object3D): THREE.Mesh[] {
    const out = this._flattenScratch;
    out.length = 0;
    obj.traverse((o) => {
      if ((o as THREE.Mesh).isMesh) out.push(o as THREE.Mesh);
    });
    return out;
  }

  /** Strip a channel's layer bit off every tracked mesh and empty the set. */
  private _clearChannel(set: Set<THREE.Mesh>, layer: number): void {
    for (const mesh of set) {
      mesh.layers.disable(layer);
    }
    set.clear();
  }

  /** Replace the primary channel with `objects` (clears the previous primary set first). */
  public setPrimary(objects: THREE.Object3D[]): void {
    this._clearChannel(this._primarySet, OUTLINE_LAYER_PRIMARY);
    for (const obj of objects) this.addPrimary(obj);
  }

  /** Replace the secondary channel with `objects` (clears the previous secondary set first). */
  public setSecondary(objects: THREE.Object3D[]): void {
    this._clearChannel(this._secondarySet, OUTLINE_LAYER_SECONDARY);
    for (const obj of objects) this.addSecondary(obj);
  }

  /** Add `obj` to the primary channel, removing each of its meshes from secondary first. */
  public addPrimary(obj: THREE.Object3D): void {
    for (const mesh of this._flatten(obj)) {
      if (this._secondarySet.has(mesh)) {
        mesh.layers.disable(OUTLINE_LAYER_SECONDARY);
        this._secondarySet.delete(mesh);
      }
      mesh.layers.enable(OUTLINE_LAYER_PRIMARY);
      this._primarySet.add(mesh);
    }
  }

  /** Add `obj` to the secondary channel, removing each of its meshes from primary first. */
  public addSecondary(obj: THREE.Object3D): void {
    for (const mesh of this._flatten(obj)) {
      if (this._primarySet.has(mesh)) {
        mesh.layers.disable(OUTLINE_LAYER_PRIMARY);
        this._primarySet.delete(mesh);
      }
      mesh.layers.enable(OUTLINE_LAYER_SECONDARY);
      this._secondarySet.add(mesh);
    }
  }

  /** Remove `obj` from the primary channel. */
  public removePrimary(obj: THREE.Object3D): void {
    for (const mesh of this._flatten(obj)) {
      mesh.layers.disable(OUTLINE_LAYER_PRIMARY);
      this._primarySet.delete(mesh);
    }
  }

  /** Remove `obj` from the secondary channel. */
  public removeSecondary(obj: THREE.Object3D): void {
    for (const mesh of this._flatten(obj)) {
      mesh.layers.disable(OUTLINE_LAYER_SECONDARY);
      this._secondarySet.delete(mesh);
    }
  }

  /**
   * Clear the primary channel; also clear the secondary channel when `alsoSecondary`.
   * (Named `clearOutlines` rather than `clear` to avoid colliding with the base
   * `Pass.clear` boolean flag.)
   */
  public clearOutlines(alsoSecondary: boolean = true): void {
    this._clearChannel(this._primarySet, OUTLINE_LAYER_PRIMARY);
    if (alsoSecondary) {
      this._clearChannel(this._secondarySet, OUTLINE_LAYER_SECONDARY);
    }
  }

  /** Snapshot of the flattened primary set (for the one reader call site). */
  public getPrimaryObjects(): THREE.Object3D[] {
    return Array.from(this._primarySet);
  }

  /** True when either channel has at least one tracked mesh (drives the enable-gate). */
  public get hasSelection(): boolean {
    return this._primarySet.size > 0 || this._secondarySet.size > 0;
  }

  /** The mask material used to warm up shader programs ahead of first use (InstancedMesh stutter). */
  public getMaskMaterialForWarmup(): THREE.ShaderMaterial {
    return this._maskMaterialPrimary;
  }

  // ===========================================================================
  // Pass implementation
  // ===========================================================================

  public render(
    renderer: THREE.WebGLRenderer,
    _writeBuffer: THREE.WebGLRenderTarget,
    readBuffer: THREE.WebGLRenderTarget,
  ): void {
    if (!this.hasSelection) return;

    // Save renderer / scene / camera state we are about to mutate.
    renderer.getClearColor(this._oldClearColor);
    const oldClearAlpha = renderer.getClearAlpha();
    const oldAutoClear = renderer.autoClear;
    const oldCameraLayerMask = this.renderCamera.layers.mask;
    const oldOverrideMaterial = this.renderScene.overrideMaterial;
    const oldBackground = this.renderScene.background;

    renderer.autoClear = false;
    this.renderScene.background = null;

    // --- 1. Mask render: draw ONLY the selected meshes (layer-filtered) --------
    renderer.setRenderTarget(this._maskRT);
    renderer.setClearColor(0x000000, 0.0);
    renderer.clear();

    if (this._primarySet.size > 0) {
      this.renderScene.overrideMaterial = this._maskMaterialPrimary;
      this.renderCamera.layers.set(OUTLINE_LAYER_PRIMARY);
      renderer.render(this.renderScene, this.renderCamera);
    }
    if (this._secondarySet.size > 0) {
      this.renderScene.overrideMaterial = this._maskMaterialSecondary;
      this.renderCamera.layers.set(OUTLINE_LAYER_SECONDARY);
      renderer.render(this.renderScene, this.renderCamera);
    }

    // Restore scene / camera state before compositing.
    this.renderCamera.layers.mask = oldCameraLayerMask;
    this.renderScene.overrideMaterial = oldOverrideMaterial;
    this.renderScene.background = oldBackground;

    // --- 2. Composite: overlay the dilated edges onto the scene color ----------
    const u = this._compositeMaterial.uniforms;
    u.tMask.value = this._maskRT.texture;
    u.uTexSize.value.set(this._maskRT.width, this._maskRT.height);
    u.uPrimaryColor.value = this.primaryColor;
    u.uSecondaryColor.value = this.secondaryColor;
    u.uThickness.value = this.edgeThickness;
    u.uOpacity.value = this.opacity;

    renderer.setRenderTarget(readBuffer);
    this._fsQuad.render(renderer);

    // Restore renderer clear state.
    renderer.setClearColor(this._oldClearColor, oldClearAlpha);
    renderer.autoClear = oldAutoClear;
  }

  /** Composer passes the effective (2× DPR) size; only the mask RT needs resizing. */
  public setSize(width: number, height: number): void {
    this._maskRT.setSize(width, height);
  }

  public dispose(): void {
    // Strip layer bits off any live meshes so a reused scene has no stale outline.
    this.clearOutlines(true);
    this._maskRT.dispose();
    this._maskMaterialPrimary.dispose();
    this._maskMaterialSecondary.dispose();
    this._compositeMaterial.dispose();
    this._fsQuad.dispose();
  }
}
